<script>
    import {
        searchAssets,
        searchProperties,
    } from "../../../../asset/assets.state.svelte.js";
    import tagService from "../../../../tag/service.js";
    import Spinner from "../../../../../components/spinner.svelte";
    import AssetList from "../../../../asset/_components/list.svelte";
    import BreadCrumbNav from "../../../../../components/breadcrum-nav.svelte";
    import powerTableService from "../../../../../services/powerTable.js";
    import PropValueRow from "../../../../../components/powerTable/prop-value-row.svelte";
    import CountByPropValue from "../../../_components/count-by-prop-value.svelte";
    import { PowerTable } from "@muonw/powertable";
    import { params, goto } from "@roxi/routify";
    import { assets_kinds } from "../../../../asset/assets.api.js";
    import { format } from "svelte-i18n";
    import { token } from "../../../../user/user.state.svelte.js";

    // --- Vanilla JS Replacements ---
    import TomSelect from "tom-select";
    import "tom-select/dist/css/tom-select.bootstrap5.css";

    const redirect = $goto;

    let kind = $derived($params.kind ? decodeURIComponent($params.kind) : "");
    let name = $derived($params.name ? decodeURIComponent($params.name) : "");

    let totalCount = $state({
        value: "Total",
        count: 0,
    });

    let tableElement = $state();

    // Active filters driven strictly by the URL
    let currentFilter = $derived.by(() => {
        const paramsCopy = JSON.parse(JSON.stringify($params || {}));
        const active = {};
        if (paramsCopy.asset__kind__in) active.asset__kind__in = paramsCopy.asset__kind__in;
        if (paramsCopy.asset_tag_id) active.asset_tag_id = paramsCopy.asset_tag_id;
        return active;
    });

    // SVELTE 5: Local UI state for form inputs before user clicks "Search"
    let filters = $state({
        asset__kind__in: [],
        asset_tag_id: ""
    });

    // Localize asset kinds for TomSelect
    let translatedAssetKinds = $derived(
        assets_kinds.map((opt) => ({
            id: opt.id,
            name: $format(`asset.kind.${opt.id}.singular`)
        }))
    );

    // Sync URL $params to local UI state
    $effect(() => {
        const paramsCopy = JSON.parse(JSON.stringify($params || {}));

        let initialKind = paramsCopy.asset__kind__in || [];
        if (typeof initialKind === "string") {
            initialKind = initialKind.split(",");
        }

        filters = {
            asset__kind__in: initialKind,
            asset_tag_id: paramsCopy.asset_tag_id || ""
        };
    });

    // --- TomSelect Svelte Action ---
    function tomSelectAction(node, params) {
        let currentValue = JSON.stringify(params.value || []);

        const formatOptions = (opts) => {
            return (opts || []).map((opt) => {
                if (typeof opt === "object" && opt !== null) {
                    return {
                        id: opt.id,
                        name: opt.name || opt.title_es || opt.title || opt.email || opt.id
                    };
                }
                return { id: opt, name: opt };
            });
        };

        const ts = new TomSelect(node, {
            plugins: ["remove_button"],
            maxItems: null,
            valueField: "id",
            labelField: "name",
            searchField: "name",
            options: formatOptions(params.options),
            create: false,
            placeholder: params.placeholder || "Buscar...",
            onChange: (val) => {
                let formattedVal = !val ? [] : Array.isArray(val) ? val : [val];
                currentValue = JSON.stringify(formattedVal);
                params.onChange(formattedVal);
            },
        });

        if (params.value && params.value.length) {
            ts.setValue(params.value, true);
        }

        return {
            update(newParams) {
                params = newParams;
                const newString = JSON.stringify(newParams.value || []);
                if (newString !== currentValue) {
                    currentValue = newString;
                    ts.setValue(newParams.value || [], true);
                }
            },
            destroy() {
                ts.destroy();
            }
        };
    }

    const handleEmptyFilters = (filtersObj = {}) => {
        const clean = {};
        for (let [key, val] of Object.entries(filtersObj)) {
            if (val !== "" && val !== null && val !== undefined) {
                clean[key] = val;
            }
        }
        return clean;
    };

    function handleFilterChange() {
        let updatedFilter = {};

        if (filters.asset__kind__in && filters.asset__kind__in.length > 0) {
            updatedFilter.asset__kind__in = filters.asset__kind__in.join(",");
        }

        if (filters.asset_tag_id) {
            updatedFilter.asset_tag_id = filters.asset_tag_id;
        }

        const cleanFilters = handleEmptyFilters(updatedFilter);
        redirect("./", { kind: kind, name: name, ...cleanFilters });
    }

    const clearFilters = () => {
        if (!currentFilter.asset__kind__in && !currentFilter.asset_tag_id) {
            return;
        }

        filters = { asset__kind__in: [], asset_tag_id: "" };
        redirect("./", { kind: kind, name: name });
    };

    let assetFilters = $derived.by(() => {
        let returnValue = {
            property_kind: kind,
            property_name: name,
        };
        if (currentFilter.asset_tag_id) {
            returnValue.tag_id = currentFilter.asset_tag_id;
        }
        if (currentFilter.asset__kind__in) {
            returnValue.kind__in = currentFilter.asset__kind__in;
        }
        return returnValue;
    });

    const parseRows = (pageContent) => {
        const cleanFilters = handleEmptyFilters(currentFilter);

        if (cleanFilters.tag_id && !cleanFilters.asset_tag_id) {
            cleanFilters.asset_tag_id = cleanFilters.tag_id;
        }
        delete cleanFilters.tag_id;

        if (cleanFilters.kind__in && !cleanFilters.asset__kind__in) {
            cleanFilters.asset__kind__in = cleanFilters.kind__in;
        }
        delete cleanFilters.kind__in;

        pageContent.forEach((row) => {
            row.value = {
                kind: kind,
                name: name,
                value: row.value || null,
                count: row.count,
                target_object_type: "asset",
                filters: { ...cleanFilters },
            };
        });
        return pageContent;
    };

    let ptOptions = $state({
        uniquePrefix: "assetPropTable",
        rowsPerPageOptions: [5, 10, 20, 50, 100],
        rowsPerPage: 5,
        footerText: false,
        footerFilters: false,
        footerLoadingBar: false,
        segments: {
            topBar: ["search"],
            pTable: ["table"],
            bottomBar: ["dropdown", "stats", "pagination"],
        },
        translations: powerTableService.getTableTranslations(),
        isDataRemote: false,
        userFunctions: {
            customParse: parseRows,
        },
    });

    let ptInstructs = [
        {
            key: "value",
            title: $format("list.columns.value"),
            filterable: false,
            parseAs: "component",
            dataComponent: PropValueRow,
        },
        {
            key: "count",
            title: $format("list.columns.asset_amount"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
    ];

    const getProps = async (kindParam, nameParam) => {
        if (!token.access_token) return [];

        const cleanFilters = handleEmptyFilters({ ...currentFilter, kind: kindParam, name: nameParam });
        let properties = await searchProperties({ filter: cleanFilters });

        searchAssets("v2/asset/", {
            filter: {
                property_kind: kindParam,
                property_name: nameParam,
                include_finding_count: true,
                ...assetFilters,
            },
            buildList: { tags: { elements: [], associations: [] } },
        });

        let total = 0;
        let data = groupPropsByValue(properties || []);

        for (let item of data) {
            item.count = item.asset_id.length;
            total = total + item.count;
        }
        data.sort((a, b) => b.count - a.count);
        totalCount.count = total;
        return data;
    };

    const getTags = async () => {
        if (!token.access_token) return [];
        let tags = await tagService.search("v2/tag/", {});
        return tags;
    };

    const groupPropsByValue = (properties = []) => {
        let groupedProps = properties.reduce((a, b) => {
            const found = a.find((e) => e.value == b.value);
            return (
                found
                    ? found.asset_id.push(b.asset_id)
                    : a.push({ ...b, asset_id: [b.asset_id] }),
                a
            );
        }, []);
        for (let prop of groupedProps) {
            let count = new Set(prop.asset_id);
            prop.asset_id = Array.from(count);
        }

        return groupedProps;
    };
</script>

<section class="content-header">
    <div class="container-fluid">
        <div class="row">
            <div class="col-6">
                <h1 class="mt-3 mb-3 page-title">{kind == name ? "" : kind + " - "} {name}</h1>
            </div>
            <div class="col-6">
                <BreadCrumbNav
                    routes={[
                        {
                            label: $format("page.properties.asset_props"),
                            path: "/property/asset",
                        },
                        {
                            label: kind,
                            path: "/property/asset/[kind]",
                            params: { kind: kind }
                        },
                        { label: name },
                    ]}
                />
            </div>
        </div>
    </div>
</section>

<section class="content">
    <div class="row">
        <div class="col-12 d-flex w-100">
            <div class="card mt-3 card-secondary card-outline no-export w-100">
                <div class="card-header">
                    <h4 class="card-title">
                        {$format("components.advancedSearch.search")}
                    </h4>
                </div>
                <div class="card-body pe-5">
                    <div class="row">
                        <div class="col-12 col-md-6 mb-3">
                            <label for="asset__kind__in" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                {$format("components.advancedSearch.asset_kind")}
                            </label>

                            <!-- SVELTE 5 UPDATE: Replaced Select2 with TomSelect -->
                            <select
                                id="asset__kind__in"
                                name="asset__kind__in"
                                multiple
                                use:tomSelectAction={{
                                    options: translatedAssetKinds,
                                    value: filters.asset__kind__in,
                                    placeholder: $format("buttons.search"),
                                    onChange: (val) => (filters.asset__kind__in = val),
                                }}
                            ></select>
                        </div>

                        <div class="col-12 col-md-6 mb-3">
                            {#await getTags()}
                                <label for="asset_tag_id" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                    {$format("components.advancedSearch.asset_tag")}
                                </label>
                                <select class="form-select" name="asset_tag_id" disabled>
                                    <option value="">--</option>
                                </select>
                            {:then tags}
                                <label for="asset_tag_id" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                    {$format("components.advancedSearch.asset_tag")}
                                </label>

                                <!-- SVELTE 5 UPDATE: Standard BS5 select -->
                                <select
                                    class="form-select"
                                    name="asset_tag_id"
                                    bind:value={filters.asset_tag_id}
                                >
                                    <option value="">--</option>
                                    {#each tags as opt}
                                        <option value={opt.id}>
                                            {opt.name}
                                        </option>
                                    {/each}
                                </select>
                            {/await}
                        </div>
                    </div>

                    <div class="btn-strip pt-3 mt-2 border-top">
                        <button class="btn btn-primary btn-sm px-4 me-2" onclick={handleFilterChange}>
                            <i class="fas fa-search me-1"></i> {$format("buttons.search")}
                        </button>
                        <button class="btn btn-light btn-sm px-4 border" onclick={clearFilters}>
                            <i class="fas fa-eraser me-1"></i> {$format("buttons.reset")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="row">
        <div class="col-12 d-flex flex-wrap flex-md-nowrap w-100" style="gap: 15px;">
            {#await getProps(kind, name)}
                <div class="card mt-3 w-100" style="min-height: 500px; display: flex; align-items: center; justify-content: center;">
                    <Spinner />
                </div>
            {:then data}
                <!-- Trigger table re-render when row count changes -->
                {#key ptOptions.rowsPerPage}
                    <div class="card mt-3 w-100" style="flex: 1;">
                        <div class="card-header">
                            <div class="card-title">
                                {$format("values")} ({totalCount.count})
                            </div>
                        </div>
                        <div class="card-body">
                            <div class="table-responsive">
                                <PowerTable
                                    ptData={data}
                                    {ptInstructs}
                                    {ptOptions}
                                    bind:this={tableElement}
                                />
                            </div>
                        </div>
                    </div>
                    <div class="card mt-3 w-100" style="flex: 1;">
                        <div class="card-header">
                            <div class="card-title">
                                {$format("page.properties.assets_per_value")}
                            </div>
                        </div>
                        <div class="card-body" style="padding: 1.5em 7em;">
                            <CountByPropValue
                                {data}
                                linkTo={"values"}
                                propertyKind={kind}
                                type={"asset"}
                                filter={currentFilter}
                            />
                        </div>
                    </div>
                {/key}
            {/await}
        </div>
    </div>

    <div class="row mt-4">
        <div class="col-12 w-100">
            {#await getTags()}
                <div class="d-flex justify-content-center"><Spinner /></div>
            {:then tags}
                <AssetList kind={"all"} {tags} showActions={false} />
            {/await}
        </div>
    </div>
</section>

<style>
    /* Bootstrap 5 theme overrides for TomSelect tags */
    :global(.ts-control) {
        border-radius: 0.375rem !important;
        padding: 0.375rem 0.75rem !important;
        min-height: 38px !important;
    }

    :global(.ts-wrapper) {
        z-index: 100;
    }

    .number {
        font-size: 2.5em;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-content: center;
        justify-content: center;
    }
</style>